import { useLocationSharing } from '@/hooks/useLocationSharing';
import { useAuth } from '@/context/AuthContext';
import { MapPin, MapPinOff, AlertTriangle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

/**
 * Compact toggle chip for staff to share/stop sharing live GPS location.
 * Shows nothing for non-trackable roles.
 */
export function LiveShareToggle() {
    const { user } = useAuth();
    const { enabled, setEnabled, lastReport, lastError, supported, permission, canTrack } = useLocationSharing(user);
    if (!canTrack) return null;

    const dotClass = !enabled ? 'bg-slate-400' : (lastError ? 'bg-rose-500' : 'bg-emerald-500 animate-pulse');
    const label = !enabled ? 'Sharing off' : (lastReport ? 'Live' : 'Starting…');

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button className='inline-flex items-center gap-2 h-8 px-2.5 rounded-full border bg-card hover:bg-muted transition text-xs' data-testid='live-share-chip'>
                    <span className={`w-2 h-2 rounded-full ${dotClass}`} />
                    {enabled ? <MapPin className='w-3.5 h-3.5' /> : <MapPinOff className='w-3.5 h-3.5' />}
                    <span className='font-medium hidden sm:inline'>{label}</span>
                </button>
            </PopoverTrigger>
            <PopoverContent align='end' className='w-80' data-testid='live-share-panel'>
                <div className='flex items-center justify-between gap-3'>
                    <div>
                        <div className='font-semibold text-sm flex items-center gap-1.5'>
                            <MapPin className='w-4 h-4 text-primary' /> Share live location
                        </div>
                        <p className='text-[11px] text-muted-foreground'>Let admin see where you are during work hours. Turn off any time.</p>
                    </div>
                    <Switch checked={enabled} onCheckedChange={setEnabled} data-testid='live-share-switch' />
                </div>
                <div className='mt-3 space-y-1 text-xs'>
                    {!supported && <div className='text-rose-600 flex items-center gap-1'><AlertTriangle className='w-3.5 h-3.5' /> Geolocation not supported on this device</div>}
                    {permission === 'denied' && <div className='text-rose-600 flex items-center gap-1'><AlertTriangle className='w-3.5 h-3.5' /> Location permission denied — enable in browser settings</div>}
                    {enabled && !lastError && lastReport && (
                        <div className='text-emerald-700 dark:text-emerald-400'>
                            ✓ Last update: {new Date(lastReport).toLocaleTimeString()}
                        </div>
                    )}
                    {enabled && lastError && <div className='text-rose-600'>Error: {lastError}</div>}
                    <div className='pt-2 border-t mt-2'>
                        <Badge variant='outline' className='text-[10px]'>Updates every 60 seconds</Badge>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default LiveShareToggle;
